/**
 * Accreditation React Hooks
 * Custom hooks for accreditation data management
 */
import { useState, useEffect, useCallback } from 'react';
import {
  listCBAccreditations,
  getCBAccreditation,
  applyCBAccreditation,
  makeCBDecision,
  checkCanIssueCertificates,
  listAuditorAccreditations,
  getAuditorAccreditation,
  applyAuditorAccreditation,
  makeAuditorDecision,
  getAuditorCompetencies,
  verifyAuditorCompetency,
  getAccreditationScopes,
  getAccreditationRules,
  getUpcomingSurveillance,
  getAccreditationSummary
} from '../api/accreditationService';

// ============================================================================
// CB ACCREDITATION HOOKS
// ============================================================================

export const useCBAccreditations = (filters = {}) => {
  const [accreditations, setAccreditations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAccreditations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listCBAccreditations(filters);
      setAccreditations(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch accreditations');
    } finally {
      setLoading(false);
    }
  }, [JSON.stringify(filters)]);

  useEffect(() => {
    fetchAccreditations();
  }, [fetchAccreditations]);

  return { accreditations, loading, error, refetch: fetchAccreditations };
};

export const useCBAccreditation = (accreditationId) => {
  const [accreditation, setAccreditation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAccreditation = useCallback(async () => {
    if (!accreditationId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getCBAccreditation(accreditationId);
      setAccreditation(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch accreditation');
    } finally {
      setLoading(false);
    }
  }, [accreditationId]);

  useEffect(() => {
    fetchAccreditation();
  }, [fetchAccreditation]);

  return { accreditation, loading, error, refetch: fetchAccreditation };
};

export const useApplyCBAccreditation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const apply = async (applicationData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await applyCBAccreditation(applicationData);
      setSuccess(true);
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { apply, loading, error, success };
};

export const useMakeCBDecision = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const makeDecision = async (accreditationId, newStatus, decisionNotes, reason) => {
    setLoading(true);
    setError(null);

    try {
      const result = await makeCBDecision(accreditationId, newStatus, decisionNotes, reason);
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to make decision');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { makeDecision, loading, error };
};

export const useCanIssueCertificates = (companyId, isoStandardId = null) => {
  const [canIssue, setCanIssue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const checkPermission = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await checkCanIssueCertificates(companyId, isoStandardId);
      setCanIssue(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to check permission');
    } finally {
      setLoading(false);
    }
  }, [companyId, isoStandardId]);

  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  return { canIssue, loading, error, refetch: checkPermission };
};

// ============================================================================
// AUDITOR ACCREDITATION HOOKS
// ============================================================================

export const useAuditorAccreditations = (userId = null) => {
  const [accreditations, setAccreditations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAccreditations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAuditorAccreditations(userId);
      setAccreditations(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch accreditations');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAccreditations();
  }, [fetchAccreditations]);

  return { accreditations, loading, error, refetch: fetchAccreditations };
};

export const useAuditorAccreditation = (accreditationId) => {
  const [accreditation, setAccreditation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAccreditation = useCallback(async () => {
    if (!accreditationId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getAuditorAccreditation(accreditationId);
      setAccreditation(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch accreditation');
    } finally {
      setLoading(false);
    }
  }, [accreditationId]);

  useEffect(() => {
    fetchAccreditation();
  }, [fetchAccreditation]);

  return { accreditation, loading, error, refetch: fetchAccreditation };
};

export const useApplyAuditorAccreditation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const apply = async (applicationData) => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const result = await applyAuditorAccreditation(applicationData);
      setSuccess(true);
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit application');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { apply, loading, error, success };
};

export const useMakeAuditorDecision = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const makeDecision = async (accreditationId, newStatus, decisionNotes, reason) => {
    setLoading(true);
    setError(null);

    try {
      const result = await makeAuditorDecision(accreditationId, newStatus, decisionNotes, reason);
      return result;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to make decision');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { makeDecision, loading, error };
};

export const useAuditorCompetencies = (userId) => {
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCompetencies = useCallback(async () => {
    if (!userId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getAuditorCompetencies(userId);
      setCompetencies(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch competencies');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchCompetencies();
  }, [fetchCompetencies]);

  return { competencies, loading, error, refetch: fetchCompetencies };
};

export const useVerifyCompetency = (userId, scopeId) => {
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const verify = useCallback(async () => {
    if (!userId || !scopeId) return;

    setLoading(true);
    setError(null);
    try {
      const data = await verifyAuditorCompetency(userId, scopeId);
      setVerification(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to verify competency');
    } finally {
      setLoading(false);
    }
  }, [userId, scopeId]);

  useEffect(() => {
    verify();
  }, [verify]);

  return { verification, loading, error, refetch: verify };
};

// ============================================================================
// SCOPES & RULES HOOKS
// ============================================================================

export const useAccreditationScopes = (isActive = true) => {
  const [scopes, setScopes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchScopes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAccreditationScopes(isActive);
      setScopes(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch scopes');
    } finally {
      setLoading(false);
    }
  }, [isActive]);

  useEffect(() => {
    fetchScopes();
  }, [fetchScopes]);

  return { scopes, loading, error, refetch: fetchScopes };
};

export const useAccreditationRules = (accreditationType = null, isActive = true) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAccreditationRules(accreditationType, isActive);
      setRules(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch rules');
    } finally {
      setLoading(false);
    }
  }, [accreditationType, isActive]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  return { rules, loading, error, refetch: fetchRules };
};

// ============================================================================
// SURVEILLANCE HOOKS
// ============================================================================

export const useUpcomingSurveillance = (daysAhead = 30) => {
  const [surveillance, setSurveillance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSurveillance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getUpcomingSurveillance(daysAhead);
      setSurveillance(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch surveillance');
    } finally {
      setLoading(false);
    }
  }, [daysAhead]);

  useEffect(() => {
    fetchSurveillance();
  }, [fetchSurveillance]);

  return { surveillance, loading, error, refetch: fetchSurveillance };
};

// ============================================================================
// DASHBOARD HOOKS
// ============================================================================

export const useAccreditationSummary = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAccreditationSummary();
      setSummary(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summary, loading, error, refetch: fetchSummary };
};
